import {z} from "zod";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {Grid, Stack, Typography} from "@mui/material";
import {Dialog} from "../base/Dialog";
import React, {useState} from "react";
import {FileDropZone} from "../base/FileDropZone";
import {useTranslation} from "react-i18next";
import {SelectInput} from "../base/SelectInput";
import {Frame} from "../base/Frame";
import {TextInput} from "../base/TextInput";
import {LabeledInput} from "../base/LabeledInput";
import {useResourceManagement} from "../../hooks/resourceHooks";
import {sendSupportMessage} from "../../services/supportService";
import {swallowException} from "../../utils/appUtils";
import {SupportIcon} from "../icons/SupportIcon";
import {ContactSupportReason} from "../../types/domain/contactSupportReason";
import {useLocalizedEnumItems} from "../../hooks/localizedEnumItemsHooks";
import {CONTACT_SUPPORT_REASONS} from "../../utils/enumItemsUtils";

export interface Props {
    open: boolean;
    onClose: (isSuccessful: boolean) => void;
}

export const ContactSupportDialog: React.FC<Props> = ({open, onClose}) => {
    const {t} = useTranslation();
    const [files, setFiles]: [File[], (value: ((prevState: File[]) => File[]) | File[]) => void] = useState<File[]>([]);
    const supportManagement = useResourceManagement({sendSupportMessage});
    const {
        control,
        handleSubmit,
        register,
        formState: {errors},
    } = useForm({
        defaultValues: {...DEFAULT_FORM_INPUTS, reason: ContactSupportReason.SUBS_SALES},
        resolver: zodResolver(FORM_SCHEMA),
    });
    const {items: contactSupportReasonOptions} = useLocalizedEnumItems(CONTACT_SUPPORT_REASONS);

    const onSubmit = (data: FormInputs) => {
        supportManagement.reset();
        const formData = new FormData();
        formData.append(
            "message",
            JSON.stringify({
                reason: data.reason,
                subject: data.subject,
                message: data.message,
            }),
        );
        Promise.all(
            files.map((file) =>
                file.arrayBuffer().then((buffer) => formData.append("attachments", new Blob([buffer]), file.name)),
            ),
        )
            .then(() => supportManagement.sendSupportMessage(formData))
            .then(() => onClose(true))
            .catch(swallowException);
    };

    return (
        <Dialog
            title={t("dialogs.contact_support.title")}
            titleIcon={
                <SupportIcon
                    sx={{
                        stroke: "#404DB8",
                        width: "1.125rem",
                        height: "1.125rem",
                    }}
                />
            }
            submitTitle={t("dialogs.actions.submit")}
            enableSubmit
            open={open}
            onClose={() => onClose(false)}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={handleSubmit(onSubmit)}
        >
            <form>
                <Stack>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Frame>
                                <Typography fontSize={16} textTransform="uppercase" sx={{marginBottom: "1rem"}}>
                                    {t("dialogs.contact_support.reason")}
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <SelectInput
                                            name="reason"
                                            control={control}
                                            options={contactSupportReasonOptions}
                                            initialValue={ContactSupportReason.SUBS_SALES}
                                            getOptionLabel={(option) => option.label}
                                        />
                                    </Grid>
                                </Grid>
                            </Frame>
                        </Grid>
                        <Grid item xs={12}>
                            <Frame>
                                <Typography fontSize={16} textTransform="uppercase" sx={{marginBottom: "1rem"}}>
                                    {t("dialogs.contact_support.your_message")}
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <LabeledInput label={t("dialogs.contact_support.subject")}>
                                            <TextInput name="subject" register={register} errors={errors} />
                                        </LabeledInput>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <LabeledInput label={t("dialogs.contact_support.message")}>
                                            <TextInput
                                                name="message"
                                                multiline={true}
                                                rows={4}
                                                register={register}
                                                errors={errors}
                                            />
                                        </LabeledInput>
                                    </Grid>
                                </Grid>
                            </Frame>
                        </Grid>
                        <Grid item xs={12}>
                            <Frame>
                                <FileDropZone files={files} setFiles={setFiles}></FileDropZone>
                            </Frame>
                        </Grid>
                    </Grid>
                </Stack>
            </form>
        </Dialog>
    );
};

interface FormInputs {
    reason: string;
    subject: string;
    message: string;
}

const FORM_SCHEMA = z.object({
    reason: z.string(),
    subject: z.string().min(1).max(200),
    message: z.string().min(1).max(1000),
});

const DEFAULT_FORM_INPUTS: FormInputs = {
    reason: "",
    subject: "",
    message: "",
};
